"use client";

import { useState, type FormEvent } from "react";
import { Check } from "lucide-react";
import {
  emptyLeadValues,
  leadSchema,
  type LeadInput,
} from "@/features/lead/schema";
import { submitLead } from "@/features/lead/submit";
import { KeycapButton } from "@/components/ui/KeycapButton";
import { cn } from "@/lib/utils";

type FieldErrors = Partial<Record<keyof LeadInput, string>>;
type Status = "idle" | "submitting" | "error" | "success" | "mailto";

type LeadFormProps = {
  headline: string;
  deliverables: readonly string[];
  complaintVolumes: readonly string[];
};

const labels: Record<keyof LeadInput, string> = {
  name: "Jméno a příjmení",
  company: "Firma",
  companyId: "IČO",
  email: "E-mail",
  phone: "Telefon",
  volume: "Kolik reklamací měsíčně řešíte?",
  assortment: "Sortiment / obchodní model",
  wantTerms: "Chci také obchodní podmínky",
  gdpr: "Souhlas se zpracováním osobních údajů",
};

function FieldError({ id, message }: { id: string; message?: string }) {
  if (!message) return null;
  return (
    <p id={id} className="mt-1.5 text-[13px] text-signal-hover">
      {message}
    </p>
  );
}

export function LeadForm({ headline, deliverables, complaintVolumes }: LeadFormProps) {
  const [values, setValues] = useState<LeadInput>(emptyLeadValues);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState<string>("");

  const update = <K extends keyof LeadInput>(key: K, value: LeadInput[K]) => {
    setValues((current) => ({ ...current, [key]: value }));
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setMessage("");

    const parsed = leadSchema.safeParse(values);
    if (!parsed.success) {
      const nextErrors: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof LeadInput | undefined;
        if (key && !nextErrors[key]) nextErrors[key] = issue.message;
      }
      setErrors(nextErrors);
      setStatus("error");
      setMessage("Formulář obsahuje chyby. Zkontrolujte prosím zvýrazněná pole.");
      return;
    }

    setErrors({});
    setStatus("submitting");

    const result = await submitLead(parsed.data);

    if (result.status === "success") {
      setStatus("success");
      setMessage("Děkujeme, poptávku jsme přijali. Ozveme se do 7 pracovních dní.");
      return;
    }

    if (result.status === "mailto") {
      setStatus("mailto");
      setMessage(
        "Otevřeli jsme váš e-mailový klient s předplněnou poptávkou. Zkontrolujte prosím zprávu a odešlete ji.",
      );
      return;
    }

    setStatus("error");
    setMessage(result.message);
  };

  const describedBy = (key: keyof LeadInput) => (errors[key] ? `${key}-error` : undefined);

  return (
    <section id="poptavka" aria-labelledby="poptavka-title" className="relative overflow-hidden">
      <div className="container-page relative z-10 pb-20 pt-24 md:pb-28 md:pt-28">
        <div className="grid grid-cols-1 gap-10 rounded-2xl border border-[var(--color-hairline)] bg-ink-700 p-6 md:p-10 lg:grid-cols-[1fr_1.1fr] lg:gap-14">
          <div>
            <h2 id="poptavka-title" className="h2-display text-text">
              {headline}
            </h2>
            <ul className="mt-8 space-y-4">
              {deliverables.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-[rgba(22,163,74,0.16)] text-valid">
                    <Check size={12} strokeWidth={3} />
                  </span>
                  <span className="text-[15px] leading-relaxed text-text-muted">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <form onSubmit={onSubmit} noValidate aria-busy={status === "submitting"}>
            {message && (
              <p
                role={status === "success" || status === "mailto" ? "status" : "alert"}
                aria-live={status === "success" || status === "mailto" ? "polite" : "assertive"}
                className={cn(
                  "mb-6 rounded-lg border px-4 py-3 text-[14px]",
                  status === "success" || status === "mailto"
                    ? "border-[rgba(22,163,74,0.4)] bg-[rgba(22,163,74,0.12)] text-text"
                    : "border-[rgba(234,108,4,0.5)] bg-[rgba(234,108,4,0.12)] text-text",
                )}
              >
                {message}
              </p>
            )}

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="name" className="mono-label text-text-muted">
                  {labels.name}
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  className="field mt-2"
                  value={values.name}
                  onChange={(event) => update("name", event.target.value)}
                  aria-invalid={errors.name ? true : undefined}
                  aria-describedby={describedBy("name")}
                />
                <FieldError id="name-error" message={errors.name} />
              </div>

              <div>
                <label htmlFor="company" className="mono-label text-text-muted">
                  {labels.company}
                </label>
                <input
                  id="company"
                  name="company"
                  type="text"
                  autoComplete="organization"
                  className="field mt-2"
                  value={values.company}
                  onChange={(event) => update("company", event.target.value)}
                  aria-invalid={errors.company ? true : undefined}
                  aria-describedby={describedBy("company")}
                />
                <FieldError id="company-error" message={errors.company} />
              </div>

              <div>
                <label htmlFor="companyId" className="mono-label text-text-muted">
                  {labels.companyId}
                </label>
                <input
                  id="companyId"
                  name="companyId"
                  type="text"
                  inputMode="numeric"
                  className="field mt-2"
                  value={values.companyId ?? ""}
                  onChange={(event) => update("companyId", event.target.value)}
                  aria-invalid={errors.companyId ? true : undefined}
                  aria-describedby={describedBy("companyId")}
                />
                <FieldError id="companyId-error" message={errors.companyId} />
              </div>

              <div>
                <label htmlFor="email" className="mono-label text-text-muted">
                  {labels.email}
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  className="field mt-2"
                  value={values.email}
                  onChange={(event) => update("email", event.target.value)}
                  aria-invalid={errors.email ? true : undefined}
                  aria-describedby={describedBy("email")}
                />
                <FieldError id="email-error" message={errors.email} />
              </div>

              <div>
                <label htmlFor="phone" className="mono-label text-text-muted">
                  {labels.phone}
                </label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  className="field mt-2"
                  value={values.phone ?? ""}
                  onChange={(event) => update("phone", event.target.value)}
                  aria-invalid={errors.phone ? true : undefined}
                  aria-describedby={describedBy("phone")}
                />
                <FieldError id="phone-error" message={errors.phone} />
              </div>

              <div>
                <label htmlFor="volume" className="mono-label text-text-muted">
                  {labels.volume}
                </label>
                <select
                  id="volume"
                  name="volume"
                  className="field mt-2"
                  value={values.volume}
                  onChange={(event) => update("volume", event.target.value)}
                  aria-invalid={errors.volume ? true : undefined}
                  aria-describedby={describedBy("volume")}
                >
                  <option value="">Vyberte…</option>
                  {complaintVolumes.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <FieldError id="volume-error" message={errors.volume} />
              </div>
            </div>

            <div className="mt-5">
              <label htmlFor="assortment" className="mono-label text-text-muted">
                {labels.assortment}
              </label>
              <textarea
                id="assortment"
                name="assortment"
                rows={4}
                className="field mt-2 resize-y"
                value={values.assortment ?? ""}
                onChange={(event) => update("assortment", event.target.value)}
                aria-invalid={errors.assortment ? true : undefined}
                aria-describedby={describedBy("assortment")}
              />
              <FieldError id="assortment-error" message={errors.assortment} />
            </div>

            <div className="mt-6 space-y-4">
              <label className="flex cursor-pointer items-start gap-3">
                <input
                  type="checkbox"
                  name="wantTerms"
                  className="mt-1 size-4 accent-[var(--color-signal)]"
                  checked={Boolean(values.wantTerms)}
                  onChange={(event) => update("wantTerms", event.target.checked)}
                />
                <span className="text-[14px] leading-relaxed text-text-muted">
                  {labels.wantTerms}
                </span>
              </label>

              <label className="flex cursor-pointer items-start gap-3">
                <input
                  type="checkbox"
                  name="gdpr"
                  required
                  className="mt-1 size-4 accent-[var(--color-signal)]"
                  checked={Boolean(values.gdpr)}
                  onChange={(event) => update("gdpr", event.target.checked)}
                  aria-invalid={errors.gdpr ? true : undefined}
                  aria-describedby={describedBy("gdpr")}
                />
                <span className="text-[14px] leading-relaxed text-text-muted">
                  Souhlasím se zpracováním osobních údajů za účelem vyřízení poptávky. Podrobnosti
                  najdete v{" "}
                  <a
                    href="/ochrana-osobnich-udaju/"
                    className="text-ice underline underline-offset-2 hover:text-text"
                  >
                    zásadách ochrany osobních údajů
                  </a>
                  .
                </span>
              </label>
              <FieldError id="gdpr-error" message={errors.gdpr} />
            </div>

            <div className="mt-8">
              <KeycapButton
                type="submit"
                variant="signal"
                disabled={status === "submitting"}
                className="w-full"
              >
                {status === "submitting" ? "Odesíláme…" : "Odeslat poptávku →"}
              </KeycapButton>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
